import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const HelpCenterPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqs: FAQItem[] = [
    {
      question: "How do I create an account?",
      answer: "Click on the 'Register' button in the top navigation. Fill in your details including name, email, password, and select whether you want to be a student or instructor. Click 'Create Account' to complete registration.",
      category: "account"
    },
    {
      question: "How do I enroll in a course?",
      answer: "Browse the course catalog, click on a course you're interested in, and click the 'Enroll' button on the course details page. You must be logged in as a student to enroll.",
      category: "courses"
    },
    {
      question: "How do I create a course as an instructor?",
      answer: "Log in as an instructor, go to your instructor dashboard, and click 'Create New Course'. Fill in the course details including title, description, category, and difficulty level.",
      category: "instructor"
    },
    {
      question: "Can I change my account type?",
      answer: "Currently, account types (student/instructor) cannot be changed after registration. Please contact support if you need to change your account type.",
      category: "account"
    },
    {
      question: "How do I track my learning progress?",
      answer: "As a student, you can view your enrolled courses and progress in your student dashboard. Progress is automatically tracked as you complete lessons.",
      category: "courses"
    },
    {
      question: "How do I upload course materials?",
      answer: "Instructors can upload course materials when creating or editing lessons. Supported file types include PDF, DOC, PPT, and various image formats.",
      category: "instructor"
    },
    {
      question: "What payment methods are accepted?",
      answer: "Currently, all courses are free. Payment integration will be added in future updates.",
      category: "payment"
    },
    {
      question: "How do I contact support?",
      answer: "You can contact our support team through the Contact page or email us directly at support@edulearn.com",
      category: "support"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'account', name: 'Account & Registration' },
    { id: 'courses', name: 'Courses & Learning' },
    { id: 'instructor', name: 'Instructor Tools' },
    { id: 'payment', name: 'Payment & Billing' },
    { id: 'support', name: 'Support & Contact' }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-lg text-gray-600 mb-8">
            Find answers to common questions and get the support you need
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Frequently Asked Questions
              {filteredFAQs.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({filteredFAQs.length} results)
                </span>
              )}
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {filteredFAQs.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600">Try adjusting your search terms or browse all categories</p>
              </div>
            ) : (
              filteredFAQs.map((faq, index) => (
                <div key={index} className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-8 bg-primary-50 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Still need help?</h3>
            <p className="text-gray-600 mb-4">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Support
              </Link>
              <a
                href="mailto:support@edulearn.com"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
